import React from "react";
import {
  Box, Button, Flex, FormControl, FormLabel, HStack, IconButton,
  Input, Select, Textarea, VStack, Badge, Divider,
  useColorModeValue,
} from "@chakra-ui/react";
import { AddIcon, DeleteIcon, ArrowUpIcon, ArrowDownIcon } from "@chakra-ui/icons";
import { SUPPORTED_LANGUAGES } from "../../lib/Languages";

const CHANNEL_OPTIONS = [
  { value: "phone", label: "Phone Call (Ezra)" },
  { value: "sms", label: "SMS (Text Message)" },
];

const UNIT_OPTIONS = [
  { value: "minutes", label: "Minutes" },
  { value: "hours", label: "Hours" },
  { value: "days", label: "Days" },
];

/**
 * DripStepsEditor — visual editor for drip campaign steps.
 *
 * Each step has: channel (phone/sms), delay, unit, and channel-specific context.
 * Phone steps have: greeting, instructions, language.
 * SMS steps have: template text (with {{placeholder}} support).
 *
 * Props:
 *   steps: Array of step objects
 *   onChange: (newSteps) => void
 */
export default function DripStepsEditor({ steps = [], onChange, handleFocus, handleBlur }) {
  const stepBg = useColorModeValue("gray.50", "gray.700");
  const borderColor = useColorModeValue("gray.200", "gray.600");

  const updateStep = (index, field, value) => {
    const BLOCKED_KEYS = new Set(['__proto__', 'constructor', 'prototype']);
    const keys = field.split(".");
    if (keys.some(k => BLOCKED_KEYS.has(k))) return;
    // Deep-clone the step to avoid mutating state in place
    const updated = steps.map((s, i) => i === index ? JSON.parse(JSON.stringify(s)) : s);
    let target = updated[index];
    for (let i = 0; i < keys.length - 1; i++) {
      if (!target[keys[i]]) target[keys[i]] = {};
      target = target[keys[i]];
    }
    target[keys[keys.length - 1]] = value;
    onChange(updated);
  };

  const addStep = () => {
    onChange([
      ...steps,
      {
        delay: 30,
        unit: "minutes",
        channel: "phone",
        phone_context: { greeting: "", instructions: "" },
        sms_context: { template: "" },
      },
    ]);
  };

  const removeStep = (index) => {
    onChange(steps.filter((_, i) => i !== index));
  };

  const moveStep = (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= steps.length) return;
    const updated = [...steps];
    [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
    onChange(updated);
    handleBlur?.(); // Close placeholder overlay — templatePath index would be stale
  };

  return (
    <VStack spacing={4} align="stretch" w="100%">
      {steps.map((step, index) => (
        <Box
          key={index}
          p={4}
          bg={stepBg}
          borderRadius="md"
          border="1px"
          borderColor={borderColor}
        >
          <Flex justify="space-between" align="center" mb={3}>
            <HStack>
              <Badge colorScheme={step.channel === "phone" ? "blue" : "green"} fontSize="sm">
                Step {index + 1}
              </Badge>
              <Badge variant="outline">{step.channel === "phone" ? "Phone Call" : "SMS"}</Badge>
            </HStack>
            <HStack spacing={1}>
              <IconButton
                icon={<ArrowUpIcon />} size="xs" variant="ghost"
                onClick={() => moveStep(index, -1)}
                isDisabled={index === 0}
                aria-label="Move up"
              />
              <IconButton
                icon={<ArrowDownIcon />} size="xs" variant="ghost"
                onClick={() => moveStep(index, 1)}
                isDisabled={index === steps.length - 1}
                aria-label="Move down"
              />
              <IconButton
                icon={<DeleteIcon />} size="xs" variant="ghost" colorScheme="red"
                onClick={() => removeStep(index)}
                aria-label="Remove step"
              />
            </HStack>
          </Flex>

          <HStack spacing={4} mb={3}>
            <FormControl w="160px">
              <FormLabel fontSize="sm">Channel</FormLabel>
              <Select
                size="sm"
                value={step.channel || "phone"}
                onChange={(e) => updateStep(index, "channel", e.target.value)}
              >
                {CHANNEL_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </Select>
            </FormControl>

            <FormControl w="100px">
              <FormLabel fontSize="sm">Delay</FormLabel>
              <Input
                size="sm"
                type="number"
                value={step.delay || 0}
                onChange={(e) => updateStep(index, "delay", parseInt(e.target.value) || 0)}
              />
            </FormControl>

            <FormControl w="130px">
              <FormLabel fontSize="sm">Unit</FormLabel>
              <Select
                size="sm"
                value={step.unit || "minutes"}
                onChange={(e) => updateStep(index, "unit", e.target.value)}
              >
                {UNIT_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </Select>
            </FormControl>
          </HStack>

          <Divider mb={3} />

          {step.channel === "phone" && (
            <VStack spacing={2} align="stretch">
              <FormControl>
                <FormLabel fontSize="sm">Greeting</FormLabel>
                <Textarea
                  size="sm" rows={2}
                  placeholder="Hi {{.PatientFirstname}}, this is {{.OfficeName}} calling about {{.AdService}}..."
                  value={step.phone_context?.greeting || ""}
                  onChange={(e) => updateStep(index, "phone_context.greeting", e.target.value)}
                  onFocus={() => handleFocus?.(["settings", "drip", "steps", index, "phone_context", "greeting"])}
                  onBlur={handleBlur}
                />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm">Extra Instructions for AI</FormLabel>
                <Textarea
                  size="sm" rows={2}
                  placeholder="If voicemail, leave a brief message with callback number."
                  value={step.phone_context?.instructions || ""}
                  onChange={(e) => updateStep(index, "phone_context.instructions", e.target.value)}
                  onFocus={() => handleFocus?.(["settings", "drip", "steps", index, "phone_context", "instructions"])}
                  onBlur={handleBlur}
                />
              </FormControl>
              <FormControl w={{ base: "100%", md: "220px" }}>
                <FormLabel fontSize="sm">Language</FormLabel>
                <Select
                  size="sm"
                  value={step.phone_context?.language || ""}
                  onChange={(e) => updateStep(index, "phone_context.language", e.target.value)}
                >
                  <option value="">Auto-detect</option>
                  {SUPPORTED_LANGUAGES.map((language) => (
                    <option key={language.value} value={language.value}>
                      {language.name}
                    </option>
                  ))}
                </Select>
              </FormControl>
            </VStack>
          )}

          {step.channel === "sms" && (
            <FormControl>
              <FormLabel fontSize="sm">SMS Template</FormLabel>
              <Textarea
                size="sm" rows={3}
                placeholder="Hi {{.PatientFirstname}}, we tried reaching you about {{.AdService}} at {{.OfficeName}}. Reply YES to schedule or call us at {{.OfficePhone}}."
                value={step.sms_context?.template || ""}
                onChange={(e) => updateStep(index, "sms_context.template", e.target.value)}
                onFocus={() => handleFocus?.(["settings", "drip", "steps", index, "sms_context", "template"])}
                onBlur={handleBlur}
              />
            </FormControl>
          )}
        </Box>
      ))}

      <Button leftIcon={<AddIcon />} size="sm" variant="outline" onClick={addStep}>
        Add Step
      </Button>
    </VStack>
  );
}
